import React, { useEffect, useMemo } from "react";

import { Redirect, Switch } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import { NoMatchRoute, RouteElement } from "@components/navigation";
import { TSection } from "@data-types/offer/live/Section";
import LiveRouteLayout from "@features/offer/desktop/layout/LiveRouteLayout";
import SearchRouteLayout from "@features/offer/desktop/layout/SearchRouteLayout";
import { useRootAppStore } from "@hooks";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { FavoritesStoreContext } from "@state/context/favorites/FavoritesStoreContext";
import PrematchOfferMenuStore from "@state/stores/offer/prematch/PrematchOfferMenuStore";
import FavoritesStore from "@state/stores/offer/shared/FavoritesStore";
import { getCurrentCulture } from "@utils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { observer } from "mobx-react";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const PrematchRouteLayout = lazy(loadFailPath, ()=>import("@features/offer/desktop/layout/PrematchRouteLayout"));

function SportsBettingLayout(props: { menuStore: PrematchOfferMenuStore }) {
	const rootStore = useRootAppStore();
	const favoritesStore = useMemo(() => new FavoritesStore(rootStore), []);

	const {
		onboardingWelcomePopupStore: { triggerWelcomePopups },
	} = rootStore;

	useEffect(() => {
		if (rootStore.userAuthStore.isLoggedIn) {
			favoritesStore.fetchUserFavorites();
		}
		return () => {
			favoritesStore.onDispose();
		};
	}, [rootStore.userAuthStore.isLoggedIn]);

	useEffect(() => {
		triggerWelcomePopups();
	}, []);

	return (
		<FavoritesStoreContext.Provider value={favoritesStore}>
			<Switch>
				<RouteElement
					exact
					path={[
						"/:culture/sports/:period",
						"/:culture/home/:period/highlights",
						"/:culture/home/:period/top-live",
						"/:culture/home/:period/last-minute",
						"/:culture/home/:period/most-played",
						"/:culture/home/:period/highlights/:sport",
						"/:culture/home/:period/top-live/:sport",
						"/:culture/home/:period/last-minute/:sport",
						"/:culture/home/:period/most-played/:sport",
						"/:culture/sports/:period/my-favorites",
						"/:culture/sports/:period/my-favorites/events",
						"/:culture/sports/:period/my-favorites/tournaments",
						"/:culture/sports/:period/f/:segments*",
						"/:culture/sports/:period/tournament/:sportAbrv/:tournamentAbrv/:tournamentId",
						"/:culture/sports/:period/promo/:tournamentIds*",
						"/:culture/sports/:period/event/:eventIds/:teamSlug?",
						"/:culture/sports/:period/event/:eventIds/:segments*",
					]}
				>
					<PrematchRouteLayout
						prematchOfferMenuStore={props.menuStore}
					/>
				</RouteElement>

				<Redirect
					exact
					from="/:culture/live/my-favorites"
					to={`/:culture/live/${TSection.MyFavoritesEvents}`}
				/>

				<RouteElement
					exact
					path={[
						"/:culture/live/:period",
						"/:culture/live/:period/f/:ids*",
						"/:culture/live/:period/:eventIds+",
					]}
				>
					<LiveRouteLayout />
				</RouteElement>

				<RouteElement exact path="/:culture/search/:period/:searchTerm">
					<SearchRouteLayout menuStore={props.menuStore} />
				</RouteElement>

				<Redirect
					exact
					from="/:culture"
					to={`/:culture/${DefaultPath.HOME}`}
				/>
				<Redirect
					exact
					from="/:culture/home"
					to={`/:culture/${DefaultPath.HOME}`}
				/>
				<Redirect
					exact
					from="/:culture/home/full"
					to={`/:culture/${DefaultPath.HOME}`}
				/>
				<Redirect
					exact
					from="/:culture/sports"
					to="/:culture/sports/full"
				/>
				<Redirect
					exact
					from="/:culture/live"
					to={`/:culture/${DefaultPath.LIVE}`}
				/>
				<Redirect
					exact
					from="/:culture/search"
					to={`/:culture/${DefaultPath.HOME}`}
				/>
				<NoMatchRoute />
			</Switch>

			<Tooltip
				id="tv-description"
				className="at-tooltipTV react-tooltip--primary"
				place="top"
				closeOnResize={true}
			/>
		</FavoritesStoreContext.Provider>
	);
}

export default observer(SportsBettingLayout);
